import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clientsAPI, type GetClientsParams } from '@offertify/api-client';
import type { Cliente } from '@offertify/core';

/**
 * Query keys for the Clienti domain. Kept identical to the keys previously used
 * inline in the web pages so cross-component cache invalidation keeps working:
 * lists start with 'clients', detail is ['client', id], etc.
 */
export const clientKeys = {
  all: ['clients'] as const,
  lists: () => ['clients'] as const,
  list: (params?: GetClientsParams) => ['clients', params] as const,
  detail: (id: string | undefined) => ['client', id] as const,
  stats: (id: string | undefined) => ['client-stats', id] as const,
  timeline: (id: string, page: number) => ['client-timeline', id, page] as const,
};

/** Paginated client list. `data` is the PaginatedResponse (data + pagination). */
export function useClients(params?: GetClientsParams) {
  return useQuery({
    queryKey: clientKeys.list(params),
    queryFn: () => clientsAPI.getClients(params).then((r) => r.data),
  });
}

/** Single client by id. Disabled until an id is available. */
export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: () => clientsAPI.getClient(id!).then((r) => r.data),
    enabled: !!id,
  });
}

/** Aggregate stats (offers, conversion, ...) for a client. */
export function useClientStats(id: string | undefined) {
  return useQuery({
    queryKey: clientKeys.stats(id),
    queryFn: () => clientsAPI.getClientStats(id!).then((r) => r.data),
    enabled: !!id,
  });
}

/**
 * One page of a client's audit timeline. Page accumulation for the
 * "load more" UX is left to the caller (it differs per platform), matching the
 * current web behavior.
 */
export function useClientTimeline(clientId: string, params: { page: number; limit: number }) {
  return useQuery({
    queryKey: clientKeys.timeline(clientId, params.page),
    queryFn: () => clientsAPI.getClientTimeline(clientId, params).then((r) => r.data),
  });
}

/** Create a client and refresh the list. UI side effects are the caller's. */
export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Cliente>) => clientsAPI.createClient(data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['inactive-clients'] });
    },
  });
}

/** Update a client and refresh all lists, its detail and stats. */
export function useUpdateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Cliente> }) =>
      clientsAPI.updateClient(id, data).then((r) => r.data),
    onSuccess: (_client, { id }) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['inactive-clients'] });
      queryClient.invalidateQueries({ queryKey: ['clienti', 'select'] });
      queryClient.invalidateQueries({ queryKey: ['clients-select'] });
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: clientKeys.stats(id) });
    },
  });
}

/** Soft-delete a client (backend marks is_deleted) and refresh the list/detail. */
export function useDeleteClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientsAPI.deleteClient(id).then((r) => r.data),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['inactive-clients'] });
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(id) });
    },
  });
}
